import React, { useEffect } from "react";
import Loading from "./components/loading";
import { getUserProfileData,getConfig,getSettings,getLandingPage } from "./utils/utils";
import CssBaseline from "@mui/material/CssBaseline";
import Y2kNeophyte from "./assets/fonts/Y2K_neophyte.ttf";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { withRouter } from "./utils/router";
import { createTheme, lighten, alpha } from "@mui/material/styles";
import { useDarkMode } from "./utils/themeMode";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FeedbackIcon from '@mui/icons-material/RateReviewOutlined';
import "./components/feedbackDialog.css";
import { themeObjectDark, themeObjectLight } from "./theme";
import { connect } from "react-redux";
import {
  setLoading,
  createSession,
  checkLocalSession,
  resetSession,
} from "./store/features/session/sessionSlice";
import { setError } from "./store/features/error/errorSlice";
import { 
  setUser,
  setLandingPage,
} from "./store/features/user/userSlice";
import { setBranding } from "./store/features/branding/brandingSlice";
import { setIssueForm } from "./store/features/issueForm/issueFormSlice";
import Login from "./views/login";
import Dashboard from "./views/dashboard";
import PrivateRoute from "./components/privateRoute";
import Toast from "./components/toast";
import { useClearCache } from "./utils/clearCache";

const mapDispatchToProps = {
  setUser,
  setLandingPage,
  setLoading,
  setBranding,
  createSession,
  checkLocalSession,
  resetSession,
  setError,
  setIssueForm,
};

const App = (props) => {
  const {
    colors,
    setUser,
    setLandingPage,
    loggedIn,
    issueForm,
    setIssueForm,
    checkLocalSession,
    resetSession,
    session,
    isLoading,
    setLoading,
    history,
    createSession,
    token,
    lastServer,
    error,
    setError,
    setBranding,
    branding,
    user,
    landingPage,
  } = props;

  const [openModal, setOpenModal] = React.useState(false);
  const handleOpenModal = () => {
    setOpenModal(true);
    window.$(function () {
      window.$('#zammad-feedback-form').ZammadForm({
        messageTitle: 'Feedback Form',
        messageSubmit: 'Send feedback',
        messageThankYou: 'Thank you for your feedback (#%s). We\'ll contact you as soon as possible.',
        modal: false,
        attachmentSupport: false,
        noCSS: false,
        debug: false,
        attributes: [
          {
            display: 'Name',
            name: 'name',
            tag: 'input',
            type: 'text',
            placeholder: 'Your Name',
            defaultValue: user.firstName + ' ' + user.lastName,
          },
          {
            display: 'Email',
            name: 'email',
            tag: 'input',
            type: 'email',
            required: true,
            placeholder: 'Your Email',
            defaultValue: user.email,
          },
          {
            display: 'Message',
            name: 'body',
            tag: 'textarea',
            required: true,
            placeholder: 'What happened, or what would you like to see?',
            defaultValue: '',
            rows: 6,
          },
        ]
      })
      // Name and email are filled in already, so start in the message. The
      // plugin draws the form after fetching its configuration.
      let tries = 0;
      const focusMessage = () => {
        const message = document.querySelector('#zammad-feedback-form textarea');
        if (message) {
          message.focus();
        } else if (++tries < 20) {
          window.setTimeout(focusMessage, 150);
        }
      };
      focusMessage();
    })
  };
  const handleCloseModal = () => {
    setOpenModal(false)
    setIssueForm({enabled: false})
  };

  // Dark when chosen in Settings, or when left to a dark system.
  const prefersDarkMode = useDarkMode();

  // Lets the browser draw its own parts (scroll bars, date pickers) to match.
  useEffect(() => {
    document.documentElement.style.colorScheme = prefersDarkMode ? "dark" : "light";
  }, [prefersDarkMode]);
  useClearCache({ duration: 60000, filename: "build.json" });
  let theme = prefersDarkMode?themeObjectDark:themeObjectLight;
  const typography = {
    fontFamily: `"Roboto", "Helvetica Nue", "Arial", sans-serif`,
    htmlFontSize: 18,
    fontSize: 18,
    body1: {
      fontFamily: "Roboto",
      color: theme.palette.text.primary,
      fontSize: "18px",
      letterSpacing: "0.016em",
      lineHeight: "24px",
      overflow: "hidden",
    },
  };
  const themeConfig = React.useMemo(
    () =>
      createTheme({
        typography,
        components: {
          // The v4 Grid forced width: 100% on containers; the current Grid does not,
          // which lets table views shrink to their content inside flex rows.
          // MUI 9 reduced ListItemIcon's min-width from 56px to 36px; keep the v4
          // width so the collapsed app-bar drawer (73px) clips the labels fully.
          MuiListItemIcon: {
            styleOverrides: {
              root: {
                minWidth: 56,
              },
            },
          },
          // v4 temporary drawers stacked at the modal z-index (above the app bar);
          // since v5 they use the drawer z-index and slide under the fixed app bar.
          MuiDrawer: {
            styleOverrides: {
              modal: ({ theme }) => ({
                zIndex: theme.zIndex.modal,
              }),
            },
          },
          MuiGrid: {
            styleOverrides: {
              container: {
                width: "100%",
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                "&.Mui-selected, &.Mui-selected:hover": {
                  // On a dark surface a light wash of the accent would be a
                  // white row; there it is the accent at low strength.
                  backgroundColor: prefersDarkMode
                    ? alpha(colors.darkMode.light, 0.14)
                    : lighten(colors.lightMode.light, 0.9),
                },
                "&.MuiTableRow-hover:hover": {
                  // On a dark surface a light wash of the accent would be a
                  // white row; there it is the accent at low strength.
                  backgroundColor: prefersDarkMode
                    ? alpha(colors.darkMode.light, 0.14)
                    : lighten(colors.lightMode.light, 0.9),
                },
              },
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              root: {
                width: "calc(1.5rem)",
                height: "calc(1.5rem)",
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                "&:hover": {
                  backgroundColor: prefersDarkMode
                    ? alpha(colors.darkMode.light, 0.2)
                    : lighten(colors.lightMode.light, 0.75),
                },
                "&.Mui-checked, &.Mui-checked:hover": {
                  "& svg": {
                    color: prefersDarkMode
                      ? colors.darkMode.light
                      : colors.lightMode.light,
                  },
                },
              },
              colorSecondary: {
                "&.Mui-checked": {
                  "&:hover": {
                    backgroundColor: prefersDarkMode
                      ? alpha(colors.darkMode.light, 0.2)
                      : lighten(colors.lightMode.light, 0.75),
                  },
                },
              },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: {
                padding: ".2em .8em",
                border: "1px solid white",
                color: "white",
                backgroundColor: lighten(theme.palette.primary.light, 0.5),
                "&.Mui-selected": {
                  backgroundColor: prefersDarkMode
                    ? colors.darkMode.light
                    : colors.lightMode.light,
                  color: "white",
                  "&:hover": {
                    backgroundColor: prefersDarkMode
                      ? colors.darkMode.light
                      : colors.lightMode.light,
                    color: "white",
                  },
                },
                "&:hover": {
                  backgroundColor: prefersDarkMode
                    ? colors.darkMode.light
                    : colors.lightMode.light,
                  color: "white",
                },
              },
            },
          },
          MuiToggleButtonGroup: {
            styleOverrides: {
              grouped: {
                "&:not(:first-of-type)": {
                  borderLeft: "1px solid white",
                },
              },
            },
          },
          // In the dark theme the tenant's main colour is a dark surface
          // colour, so controls that would be drawn in it use the bright
          // accent instead. The light theme keeps the library's choice.
          ...(prefersDarkMode ? {
            MuiSwitch: {
              styleOverrides: {
                switchBase: {
                  "&.Mui-checked": {
                    color: colors.darkMode.light,
                  },
                  "&.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: colors.darkMode.light,
                  },
                },
              },
            },
            MuiRadio: {
              styleOverrides: {
                root: {
                  "&.Mui-checked": {
                    color: colors.darkMode.light,
                  },
                },
              },
            },
            MuiFormLabel: {
              styleOverrides: {
                root: {
                  "&.Mui-focused": {
                    color: colors.darkMode.light,
                  },
                },
              },
            },
            MuiButton: {
              styleOverrides: {
                root: ({ ownerState }) => (
                  ownerState.color === "primary" && ownerState.variant !== "contained"
                    ? {
                        color: colors.darkMode.light,
                        ...(ownerState.variant === "outlined"
                          ? { borderColor: alpha(colors.darkMode.light, 0.5) }
                          : {}),
                      }
                    : {}
                ),
              },
            },
            // The round "+" that opens a row's actions.
            MuiFab: {
              styleOverrides: {
                root: ({ ownerState }) => (
                  ownerState.color === "primary"
                    ? {
                        color: "#fff",
                        backgroundColor: alpha(colors.darkMode.light, 0.85),
                        "&:hover": {
                          backgroundColor: colors.darkMode.light,
                        },
                      }
                    : {}
                ),
              },
            },
            // The row actions sit on the table row; on a dark row their
            // shadow does not show, so the circle itself is a step lighter.
            MuiSpeedDialAction: {
              styleOverrides: {
                fab: {
                  color: "rgba(255, 255, 255, 0.85)",
                  backgroundColor: "#383d46",
                  "&:hover": {
                    backgroundColor: "#454b56",
                  },
                },
              },
            },
            MuiCircularProgress: {
              styleOverrides: {
                colorPrimary: {
                  color: colors.darkMode.light,
                },
              },
            },
            MuiSlider: {
              styleOverrides: {
                colorPrimary: {
                  color: colors.darkMode.light,
                },
              },
            },
          } : {}),
          MuiTabs: {
            styleOverrides: {
              root: {
                color: theme.palette.text.secondary,
              },
              indicator: {
                backgroundColor: prefersDarkMode
                  ? colors.darkMode.light
                  : colors.lightMode.light,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                "&.Mui-selected": {
                  color: prefersDarkMode
                    ? colors.darkMode.light
                    : colors.lightMode.light,
                },
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled,
                },
              },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              "@font-face": {
                fontFamily: "Y2K Neophyte",
                src: `url(${Y2kNeophyte}) format("truetype")`,
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              notchedOutline: {
                borderWidth: "thin",
                top: "-4px",
              },
              root: {
                "&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.light,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.light,
                },
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              input: {
                "&:disabled": {
                  WebkitTextFillColor: theme.palette.text.disabled,
                },
              },
            },
          },
        },
        palette: {
          ...theme.palette,
          mode: prefersDarkMode ? "dark" : "light",
          primary: {
            ...theme.palette.primary,
            light: prefersDarkMode
              ? colors.darkMode.light
              : colors.lightMode.light,
            main: prefersDarkMode
              ? colors.darkMode.main
              : colors.lightMode.main,
            dark: prefersDarkMode
              ? colors.darkMode.dark
              : colors.lightMode.dark,
          },
          // The brand tone follows the tenant's colours: its main colour on
          // a light surface, its bright accent on a dark one.
          tone: {
            ...theme.palette.tone,
            brand: {
              main: prefersDarkMode ? colors.darkMode.light : colors.lightMode.main,
              soft: prefersDarkMode
                ? alpha(colors.darkMode.light, 0.16)
                : alpha(colors.lightMode.light, 0.13),
            },
          },
        },
      }),
    // eslint-disable-next-line
    [prefersDarkMode, branding]
  );

  let location = useLocation();
  // URL the user opened before any login redirect, so a page reload lands back on it.
  const initialPathRef = React.useRef(window.location.pathname + window.location.search);
  const values = {
    server: lastServer,
    token: session.token,
  };

  const loginFailed = () => {
    setLoading(false);
    resetSession();
    history.push("/login");
  };

  const getBrandingData = async () => {
    try{
      let fetchConfig = await getConfig(values, setBranding);
      if(fetchConfig instanceof Error) throw fetchConfig.message;

      setBranding(fetchConfig);
    }catch(error){
      setError({
        visible: true,
        type: "error",
        message: error,
      });
    }
    // eslint-disable-next-line
  };

  const refreshUserProfile = () => {
    (async function attemptGetProfile() {
      try {
        let profile = await getUserProfileData(values, setUser);

        if (profile && profile.userProfile) {
          createSession({ ...session, token: token, server: lastServer, loggedIn: true });

          const restoredLandingPage = landingPage || getLandingPage(profile.userProfile);
          if (restoredLandingPage !== landingPage) setLandingPage(restoredLandingPage);

          const requested = location.state?.from?.pathname || initialPathRef.current;
          const target =
            requested && requested.startsWith("/dashboard/") && requested !== "/dashboard/"
              ? requested
              : `/dashboard/${restoredLandingPage}`;
          history.push(target, { initialLoad: true });
          setLoading(false);
        } else {
          throw new Error(
            "Failed to fetch user data. Please login to continue."
          );
        }
      } catch (error) {
        loginFailed();
        console.warn(error);
      }
    })();
    return false;
  };

  useEffect(() => {
    if (!loggedIn && isLoading === false && lastServer === "") {
      (async function attemptSessionRestore() {
        try {
          await checkLocalSession();
          if (localStorage.getItem("mcl-appData") === null) {
            loginFailed();
          } else {
            setLoading(true);
          }
        } catch (error) {
          console.warn(error);
        }
      })();
    }
    // eslint-disable-next-line
  }, [loggedIn]);

  useEffect(()=>{
    if (branding.default === "yes") {
      getSettings(values, setBranding);
    }
    // eslint-disable-next-line
  },[loggedIn])

  useEffect(() => {
    const minHeight = 700;
    const enforceMinHeight = () => {
      if (window.innerHeight < minHeight) {
        window.resizeTo(window.innerWidth, minHeight);
      }
    };
    window.addEventListener('resize', enforceMinHeight);
    return () => window.removeEventListener('resize', enforceMinHeight);
  }, [])

  useEffect(() => {
    if(isLoading && !loggedIn && lastServer !== ""){
      getBrandingData();
      switch (token) {
        case "":
          try {
            loginFailed();
            if (session.username === "" || session.user_display_name === "") {
              throw new Error("Session not found. Please login to continue.");
            } else {
              throw new Error(
                "Session Expired. Please login again to continue."
              );
            }
          } catch (error) {
            console.warn(error);
          }
          break;
        default:
          refreshUserProfile();
          break;
      }
    }
    // eslint-disable-next-line
  }, [session, loggedIn]);
  
  useEffect(() => {
    if (issueForm.enabled) { 
      handleOpenModal()
    }
     // eslint-disable-next-line
  }, [issueForm.enabled]);

  // Brand colour for the feedback dialog, which renders outside the theme.
  const brandColors = colors ? (prefersDarkMode ? colors.darkMode : colors.lightMode) : null;
  const feedbackAccent = (brandColors && brandColors.light) || "#01BAEF";

  return (
    <div>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="feedback-dialog-title"
      >
        <div className={prefersDarkMode ? "feedback-dialog feedback-dialog--dark" : "feedback-dialog"} style={{ "--feedback-accent": feedbackAccent }} tabIndex={-1}>
          <div className="feedback-dialog__header">
            <div className="feedback-dialog__badge"><FeedbackIcon /></div>
            <div className="feedback-dialog__heading">
              <h2 id="feedback-dialog-title" className="feedback-dialog__title">Send feedback</h2>
              <p className="feedback-dialog__subtitle">
                Tell us what is working and what is not. We reply by email.
              </p>
            </div>
            <IconButton aria-label="close" size="small" onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </div>
          {/* Filled in by the Zammad form plugin, see handleOpenModal. */}
          <div className="feedback-dialog__body" id="zammad-feedback-form" />
          <p className="feedback-dialog__note">
            Your name and email are sent with the message so we can reply.
          </p>
        </div>
      </Modal>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={themeConfig}>
          <CssBaseline />
          {error.visible ? (
            <Toast
              message={error.message}
              severity={error.type}
              setOpen={setError}
              open={error.visible}
            />
          ) : null}
          {isLoading ? <Loading /> : null}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Navigate to={`/dashboard/${landingPage}`} replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/*"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="*"
              element={
                loggedIn ? (
                  <Navigate to={`/dashboard/${landingPage}`} replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    issueForm: state.issueForm,
    error: state.error,
    user: state.user,
    landingPage: state.user.landingPage,
    session: state.session,
    isLoading: state.session.isLoading,
    token: state.session.token,
    lastServer: state.session.lastServer,
    loggedIn: state.session.loggedIn,
    colors: state.branding.colors,
    branding: state.branding,
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
